import React, {
  useState,
  type FocusEvent,
  type HTMLInputTypeAttribute,
} from 'react';
import { withMask } from 'use-mask-input';
import { Input as InputUI } from '@/components/ui/input';

type InputKind =
  | 'phone_number'
  | 'date'
  | 'ssn'
  | 'zipcode'
  | 'account_number'
  | 'routing_number';

interface InputComponentProps
  extends React.InputHTMLAttributes<HTMLInputElement> {
  kind?: InputKind;
  hideValueOnBlur?: boolean;
  error?: string;
}

const Input = React.forwardRef<HTMLInputElement, InputComponentProps>(
  ({ kind, hideValueOnBlur, type = 'text', onBlur, onFocus, ...props }, ref) => {
    const [dynamicInputType, setDynamicInputType] =
      useState<HTMLInputTypeAttribute>(type);

    const handleBlur = (e: FocusEvent<HTMLInputElement>) => {
      setDynamicInputType('password');
      onBlur?.(e);
    };

    const handleFocus = (e: FocusEvent<HTMLInputElement>) => {
      setDynamicInputType('text');
      onFocus?.(e);
    };

    const refWithMask = kind ? getRefWitMask(kind) : ref;

    return (
      <InputUI
        ref={refWithMask}
        onBlur={handleBlur}
        onFocus={handleFocus}
        type={hideValueOnBlur ? dynamicInputType : type}
        {...props}
      />
    );
  }
);
Input.displayName = 'Input';

function getRefWitMask(kind: InputKind) {
  switch (kind) {
    case 'phone_number':
      return withMask('+1 (999) 999-9999', {
        autoUnmask: true,
        inputmode: 'tel',
      });

    case 'date':
      return withMask('99/99/9999', {
        inputmode: 'numeric',
      });

    case 'ssn':
      return withMask('999-99-9999', {
        autoUnmask: true,
        inputmode: 'numeric',
      });

    case 'zipcode':
      return withMask('99999', {
        autoUnmask: true,
        inputmode: 'numeric',
      });

    case 'account_number':
      return withMask('9{6,17}', {
        autoUnmask: true,
        inputmode: 'numeric',
        placeholder: '',
      });

    case 'routing_number':
      return withMask('9{9}', {
        autoUnmask: true,
        inputmode: 'numeric',
      });
  }
}

export { Input };
